import React, { useState } from "react";
import styles from './CategoryPerformance.module.scss';
import Tag from '../tag/Tag';
import Dropdown from '../dropdown/Dropdown';
import ProfilePicture from '../profilePicture/ProfilePicture';
import PropTypes from "prop-types";

const viewItemShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  cumulativeScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  profilePicture: PropTypes.string,
});

const viewShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(viewItemShape).isRequired,
  type: PropTypes.oneOf(['tag', 'name', 'instructor']),
  itemsPerRow: PropTypes.number,
});

// Multi-view mode: renders content for the active view
const ViewContent = ({ view }) => {
  const { data, type, itemsPerRow = 4 } = view;

  if (!data || data.length === 0) {
    return <div className={styles.emptyState}><p>No data available.</p></div>;
  }

  return (
    <div className={styles.categoriesGrid} style={{ '--grid-columns': `repeat(${itemsPerRow}, 1fr)` }}>
      {data.map((item, index) => (
        <div key={index} className={styles.categoryCard}>
          {type === 'tag' && <Tag text={item.name} variant={item.variant} />}
          {type === 'instructor' && (
            <div className={styles.instructorItem}>
              <ProfilePicture
                firstName={item.firstName}
                lastName={item.lastName}
                src={item.profilePicture}
                size="xsm"
              />
              <span className={styles.instructorName}>{item.name}</span>
            </div>
          )}
          {(type === 'name' || !type) && (
            <span className={styles.categoryName}>{item.name}</span>
          )}
          <div className={styles.scoreInfo}>
            <span className={styles.scoreLabel}>Cumulative Score</span>
            <span className={styles.scoreValue}>{item.cumulativeScore}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

ViewContent.propTypes = {
  view: viewShape.isRequired,
};

const CategoryPerformance = ({
  // Legacy single-view props
  data = [],
  useTag = true,
  title = "Category based Performance",
  itemsPerRow = 4,
  // Multi-view props
  views,
}) => {
  const [selectedView, setSelectedView] = useState(views?.[0]?.value ?? null);

  // Multi-view mode
  if (views && views.length > 0) {
    const activeView = views.find(v => v.value === selectedView) || views[0];
    const dropdownOptions = views.map(v => ({ label: v.label, value: v.value }));

    return (
      <div className={styles.categoryPerformanceBox}>
        <div className={styles.multiViewHeader}>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.viewDropdown}>
            <Dropdown
              options={dropdownOptions}
              value={selectedView}
              onChange={setSelectedView}
              size="small"
            />
          </div>
        </div>
        <ViewContent view={activeView} />
      </div>
    );
  }

  // Legacy single-view mode (backward compat)
  return (
    <div className={styles.categoryPerformanceBox}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.categoriesGrid} style={{ '--grid-columns': `repeat(${itemsPerRow}, 1fr)` }}>
        {data.map((item, index) => (
          <div key={index} className={styles.categoryCard}>
            {useTag ? (
              <Tag text={item.name} variant={item.variant} />
            ) : (
              <span className={styles.categoryName}>{item.name}</span>
            )}
            <div className={styles.scoreInfo}>
              <span className={styles.scoreLabel}>Cumulative Score</span>
              <span className={styles.scoreValue}>{item.cumulativeScore}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

CategoryPerformance.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    cumulativeScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    variant: PropTypes.number,
  })),
  useTag: PropTypes.bool,
  title: PropTypes.string,
  itemsPerRow: PropTypes.number,
  views: PropTypes.arrayOf(viewShape),
};

export default CategoryPerformance;
